import {
  Box,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { AcquirerTypes } from '../../enums/acquirer-types.enum';
import { UserManagementFilterMode } from '../../enums/misc.enum';
import { updateOnboardingStatus } from '../../helpers/onboarding-status';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  BasicMerchant,
  MerchantOnboardingStatusAction,
  RetrieveMerchantResponse,
} from '../../type/admin.types';
import { DefaultEntityCheckResponse } from '../../type/entity.types';
import { ErrorResponse } from '../../type/error-response.types';
import {
  UserManagementFilterData,
  UserManagementFilterForm,
} from '../../type/filter.types';
import {
  MdrFee,
  UpdateOnboardingStatusRequest,
} from '../../type/onboard-merchant-request.types';
import AlternativeUserFilter from '../filters/alternative-merchant-management-filter.component';

interface Props {
  isApproveModalOpen: boolean;
  setIsApproveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entity: BasicMerchant | RetrieveMerchantResponse;
  updateTable: React.Dispatch<React.SetStateAction<boolean>>;
  acquirerColumn?: AcquirerTypes;
}


function ApproveEntityModal(props: Props) {
  const {
    isApproveModalOpen,
    setIsApproveModalOpen,
    entity,
    updateTable,
    acquirerColumn,
  } = props;


  const axiosPrivate = useAxiosPrivate();
  const [isLoading, setIsLoading] = useState(false);
  const filterValues = useForm<UserManagementFilterForm>({
    initialValues: {
      partner: '',
      reseller: '',
      merchant: '',
      merchantBranch: '',
    },
  });
  const [mdrFees, setMdrFees] = useState<MdrFee[]>();
  const [isLoadPartners, setIsLoadPartners] = useState<boolean>(true);
  const filterSelectData = useForm<UserManagementFilterData>({
    initialValues: {
      partnersData: [],
      resellersData: [],
      merchantsData: [],
      merchantBranchesData: [],
    },
  });

  const [isDefaultEntity, setIsDefaultEntity] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  useEffect(() => {
    const getAndSetIsDefaultEntity = async () => {
      try {
        setIsModalLoading(true);
        const res = await axiosPrivate.get(
          `/web/onboard/${entity.merchantId}/default_entity_check`
        );
        const data: DefaultEntityCheckResponse = res.data;

        setIsDefaultEntity(data.isDefaultEntity);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-onboardingData',
          message: error.error || 'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsModalLoading(false);
      }
    };

    if (isApproveModalOpen) {
      getAndSetIsDefaultEntity();
    }
  }, [isApproveModalOpen]);

  const handleSubmit = async () => {
    let request: UpdateOnboardingStatusRequest = {
      onboardingStatus: MerchantOnboardingStatusAction.APPROVE,
      merchantIds: [entity.merchantId],
      mdrFees: [],
    };

    request.mdrFees = mdrFees || [];

    if (filterValues.values.partner) {
      request.partnerId = parseInt(filterValues.values.partner);
    }
    if (filterValues.values.reseller) {
      request.resellerId = parseInt(filterValues.values.reseller);
    }
    if (filterValues.values.merchant) {
      request.merchantId = parseInt(filterValues.values.merchant);
    }

    try {
      setIsLoading(true);

      await updateOnboardingStatus(request, acquirerColumn);
      filterValues.reset();
      updateTable((prev) => !prev);
      handleClose();
    } catch (err: any) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsApproveModalOpen(false);
    filterValues.reset();
  };

  enum FilterNames {
    PARTNER = 'PARTNER',
    RESELLER = 'RESELLER',
    MERCHANT = 'MERCHANT',
    BRANCH = 'BRANCH',
  }

  const getNameOfEntity = (type: FilterNames): string => {
    let name: string = '';
    if (type === FilterNames.BRANCH) {
      name =
        filterSelectData.values.merchantBranchesData.find((data) => {
          return data.value === filterValues.values.merchantBranch;
        })?.label ?? 'Unknown';
    } else if (type === FilterNames.MERCHANT) {
      name =
        filterSelectData.values.merchantsData.find((data) => {
          return data.value === filterValues.values.merchant;
        })?.label ?? 'Unknown';
    } else if (type === FilterNames.RESELLER) {
      name =
        filterSelectData.values.resellersData.find((data) => {
          return data.value === filterValues.values.reseller;
        })?.label ?? 'Unknown';
    } else if (type === FilterNames.PARTNER) {
      name =
        filterSelectData.values.partnersData.find((data) => {
          return data.value === filterValues.values.partner;
        })?.label ?? 'Unknown';
    } else {
      name = 'Unknown';
    }
    return name;
  };

  const getAlertMessage = (): JSX.Element => {
    if (entity.dateApproved || !isDefaultEntity) {
      return (
        <Text>
          Merchant will be approved under the same previously selected entities
        </Text>
      );
    }

    let message: JSX.Element;

    if (filterValues.values.merchant) {
      message = (
        <>
          Merchant will be approved as a <strong>MERCHANT BRANCH</strong> of
          Partner: <strong>{getNameOfEntity(FilterNames.PARTNER)}</strong> &gt;
          Reseller: <strong>{getNameOfEntity(FilterNames.RESELLER)}</strong>{' '}
          &gt; Merchant:{' '}
          <strong>{getNameOfEntity(FilterNames.MERCHANT)}</strong>
        </>
      );
    } else if (filterValues.values.reseller) {
      message = (
        <>
          Merchant will be approved as a <strong>MERCHANT</strong> of Partner:{' '}
          <strong>{getNameOfEntity(FilterNames.PARTNER)}</strong> &gt; Reseller:{' '}
          <strong>{getNameOfEntity(FilterNames.RESELLER)}</strong>
        </>
      );
    } else if (filterValues.values.partner) {
      message = (
        <>
          Merchant will be approved as a <strong>RESELLER</strong> of Partner:{' '}
          <strong>{getNameOfEntity(FilterNames.PARTNER)}</strong>
        </>
      );
    } else {
      message = (
        <>
          Merchant will be approved as a <strong>PARTNER</strong>
        </>
      );
    }
    return <Text>{message}</Text>;
  };

  return (
    <Modal
      size="xl"
      opened={isApproveModalOpen}
      onClose={handleClose}
      centered
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
      withCloseButton={false}
    >
      <Stack p="xl">
        {isModalLoading ? (
          <Center>
            <Loader />
          </Center>
        ) : (
          <>
            <Group>
              <Box
                sx={() => ({
                  borderRadius: 8,
                  backgroundColor: '#4F4F4F',
                  color: '#fff',
                  height: '3.81rem',
                  width: '3.81rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                <Check />
              </Box>
              <Stack spacing="xs">
                <Title>Approve Merchant</Title>
                <Text>{getAlertMessage()}</Text>
              </Stack>
            </Group>

            <Divider mt={4} mb={4} />

            {!entity.dateApproved && isDefaultEntity && (
              <AlternativeUserFilter
                filterValues={filterValues}
                setIsLoadPartners={setIsLoadPartners}
                isLoadPartners={isLoadPartners}
                filterSelectData={filterSelectData}
                mode={UserManagementFilterMode.APPROVE_ENTITY_MODE}
                mdrFees={mdrFees}
                setMdrFees={setMdrFees}
              />
            )}

            <Group mt={8} grow>
              <Button
                variant="default"
                onClick={handleClose}
                disabled={isLoading}
                data-cy="closeButton"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="green"
                fullWidth
                loading={isLoading}
              >
                Approve
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default ApproveEntityModal;
